import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, Package, Pencil, Plus, Trash2 } from "lucide-react";

import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { TableCard } from "@/components/shared/TableCard";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { clientKeys } from "@/lib/queryKeys";
import {
  createClientCatalogItem,
  deleteClientCatalogItem,
  listClientCatalogItems,
  updateClientCatalogItem,
  type ClientCatalogItem,
} from "@/services/clientCatalogService";
import type { TaxCategory } from "@fiscalo/shared-types";
import {
  findTaxOptionByCategoryAndRate,
  taxOptionsForRegime,
} from "@/lib/taxOptions";
import { useTenantSpecialRegime } from "@/lib/useTenantSpecialRegime";

/** Shared list — see lib/taxOptions. A catalog row stores a bare category, so
 *  it gets the standard set; régimen rates are a per-line choice. */

export interface ClientCatalogTabProps {
  clientId: string;
}

export function ClientCatalogTab({ clientId }: ClientCatalogTabProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Régimen-aware: an automotora may catalogue a vehicle at 2,2%. Options
  // carry the RATE, which is what the category alone cannot express.
  const specialRegime = useTenantSpecialRegime();
  const TAX_OPTIONS = taxOptionsForRegime(specialRegime);

  const { data: items = [], isLoading } = useQuery({
    queryKey: clientKeys.catalog(clientId),
    queryFn: () => listClientCatalogItems(clientId),
    enabled: Boolean(clientId),
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ClientCatalogItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<ClientCatalogItem | null>(null);
  const [sku, setSku] = useState("");
  const [description, setDescription] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  // The OPTION id, not the category: 2,2% and 2,75% are both vat_other.
  const [taxOptionId, setTaxOptionId] = useState<string>("vat_basic");

  /**
   * MUST stay below `taxOptionId`. This read used to sit above the state
   * declarations, where it threw `ReferenceError: Cannot access 'taxOptionId'
   * before initialization` on every render — the whole tab was a blank screen
   * from 201ae542 (2026-09-02) until it was reported two months later.
   *
   * `tsc` does not catch it. TS2448 fires on a DIRECT use before declaration,
   * but this one hides inside the arrow function handed to `.find()`, and the
   * compiler has to assume a callback may run later. `.find()` calls it
   * synchronously, so the temporal dead zone is real at runtime and invisible
   * at build time.
   */
  const selectedTaxOption =
    TAX_OPTIONS.find((o) => o.id === taxOptionId) ?? TAX_OPTIONS[0];

  const resetForm = useCallback(() => {
    setSku("");
    setDescription("");
    setUnitPrice("");
    setTaxOptionId("vat_basic");
    setEditing(null);
  }, []);

  const openCreate = useCallback(() => {
    resetForm();
    setDialogOpen(true);
  }, [resetForm]);

  const openEdit = useCallback((row: ClientCatalogItem) => {
    setEditing(row);
    setSku(row.sku ?? "");
    setDescription(row.description);
    setUnitPrice(String(row.unitPrice));
    // Recover the option the row was saved with; category + rate is what
    // distinguishes 2,2% from 2,75%.
    setTaxOptionId(
      findTaxOptionByCategoryAndRate(
        row.defaultTaxCategory as TaxCategory,
        row.defaultTaxRate,
      )?.id ?? "vat_basic",
    );
    setDialogOpen(true);
  }, []);

  const createMut = useMutation({
    mutationFn: () =>
      createClientCatalogItem(clientId, {
        sku: sku.trim() || undefined,
        description: description.trim(),
        unitPrice: Number(unitPrice),
        defaultTaxCategory: selectedTaxOption.category,
        defaultTaxRate: selectedTaxOption.rate,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: clientKeys.catalog(clientId) });
      toast.success(t("clients.catalog.toast.created"));
      setDialogOpen(false);
      resetForm();
    },
    onError: () => {
      toast.error(t("clients.catalog.toast.saveError"));
    },
  });

  const updateMut = useMutation({
    mutationFn: () =>
      updateClientCatalogItem(clientId, editing!.id, {
        sku: sku.trim() || null,
        description: description.trim(),
        unitPrice: Number(unitPrice),
        defaultTaxCategory: selectedTaxOption.category,
        defaultTaxRate: selectedTaxOption.rate,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: clientKeys.catalog(clientId) });
      toast.success(t("clients.catalog.toast.updated"));
      setDialogOpen(false);
      resetForm();
    },
    onError: () => {
      toast.error(t("clients.catalog.toast.saveError"));
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteClientCatalogItem(clientId, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: clientKeys.catalog(clientId) });
      toast.success(t("clients.catalog.toast.deleted"));
      setDeleteItem(null);
    },
    onError: () => {
      toast.error(t("clients.catalog.toast.deleteError"));
    },
  });

  const handleSubmit = (): void => {
    const price = Number(unitPrice);
    if (!description.trim()) return;
    if (!Number.isFinite(price) || price < 0) return;
    if (editing) updateMut.mutate();
    else createMut.mutate();
  };

  const saving = createMut.isPending || updateMut.isPending;

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            className="transition-colors duration-150"
            onClick={openCreate}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            {t("clients.catalog.add")}
          </Button>
        </div>
        <TableCard>
        {isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Package />}
            title={t("clients.catalog.empty")}
            message={t("clients.catalog.emptyHint")}
            action={
              <Button type="button" onClick={openCreate} className="transition-colors duration-150">
                <Plus className="mr-2 h-4 w-4" aria-hidden />
                {t("clients.catalog.add")}
              </Button>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("clients.catalog.columns.id")}</TableHead>
                <TableHead>{t("clients.catalog.columns.description")}</TableHead>
                <TableHead className="text-right">{t("clients.catalog.columns.unitPrice")}</TableHead>
                <TableHead>{t("clients.catalog.columns.tax")}</TableHead>
                <TableHead className="w-[100px] text-right">{t("clients.catalog.columns.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-sm text-muted-foreground">
                    {row.sku?.trim() || "—"}
                  </TableCell>
                  <TableCell className="font-medium text-foreground">{row.description}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.unitPrice.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 4,
                    })}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {t(
                      TAX_OPTIONS.find((o) => o.category === row.defaultTaxCategory)?.labelKey ??
                        "invoices.new.taxOptionVatBasic"
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 transition-colors duration-150"
                        onClick={() => openEdit(row)}
                        aria-label={t("clients.catalog.edit")}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive transition-colors duration-150 hover:text-destructive"
                        onClick={() => setDeleteItem(row)}
                        aria-label={t("clients.catalog.delete")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        </TableCard>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {editing ? t("clients.catalog.dialogEditTitle") : t("clients.catalog.dialogCreateTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="catalog-sku">{t("clients.catalog.fields.sku")}</Label>
              <Input
                id="catalog-sku"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                placeholder={t("clients.catalog.fields.skuPlaceholder")}
                maxLength={64}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="catalog-desc">{t("clients.catalog.fields.description")}</Label>
              <Input
                id="catalog-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("clients.catalog.fields.descriptionPlaceholder")}
                maxLength={500}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="catalog-price">{t("clients.catalog.fields.unitPrice")}</Label>
              <Input
                id="catalog-price"
                type="number"
                min={0}
                step="0.01"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("clients.catalog.fields.defaultTax")}</Label>
              <Select
                value={taxOptionId}
                onValueChange={setTaxOptionId}
              >
                <SelectTrigger id="catalog-tax" className="transition-colors duration-150">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAX_OPTIONS.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {t(o.labelKey, o.fallback)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDialogOpen(false);
                resetForm();
              }}
            >
              {t("clients.form.cancel", "Cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={
                saving ||
                !description.trim() ||
                !Number.isFinite(Number(unitPrice)) ||
                Number(unitPrice) < 0
              }
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("clients.catalog.saving")}
                </>
              ) : editing ? (
                t("clients.catalog.save")
              ) : (
                t("clients.catalog.create")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={deleteItem != null}
        onOpenChange={(open) => !open && setDeleteItem(null)}
        title={t("clients.catalog.deleteConfirmTitle")}
        description={t("clients.catalog.deleteConfirmDescription")}
        confirmText={t("clients.catalog.delete")}
        cancelText={t("clients.form.cancel", "Cancel")}
        loadingText={t("clients.catalog.deleting")}
        variant="destructive"
        isLoading={deleteMut.isPending}
        onConfirm={() => {
          if (deleteItem) deleteMut.mutate(deleteItem.id);
        }}
      />
    </>
  );
}
